import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, ShoppingCart, TrendingUp, BarChart3, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIODS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '365d', label: '1 Year' },
  { value: 'all', label: 'All Time' },
];

const BarChart = ({ data, dataKey, color = '#00F0FF', height = 220 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-[#A1A7B5] text-sm" style={{ height }}>
        No data for this period
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => d[dataKey] || 0), 1);
  const barWidth = Math.max(Math.min(Math.floor((100 / data.length) - 1), 12), 2);

  return (
    <div className="flex items-end justify-between gap-[2px] px-1" style={{ height }} data-testid="bar-chart">
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const pct = (val / maxVal) * 100;
        return (
          <div key={i} className="flex flex-col items-center flex-1 group relative">
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-[#1E2532] text-white text-xs px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg border border-[#2a3040]">
                <p className="font-semibold">{dataKey === 'revenue' ? `$${val.toFixed(2)}` : val}</p>
                <p className="text-[#A1A7B5] text-[10px]">{d._id}</p>
              </div>
            </div>
            {/* Bar */}
            <div
              className="w-full rounded-t transition-all duration-300 hover:opacity-80 cursor-pointer"
              style={{
                height: `${Math.max(pct, 2)}%`,
                backgroundColor: color,
                minHeight: '3px',
                maxWidth: `${barWidth}px`,
                margin: '0 auto'
              }}
            />
            {/* Label - show for sparse data */}
            {data.length <= 14 && (
              <span className="text-[8px] text-[#A1A7B5] mt-1 truncate w-full text-center">
                {d._id?.slice(5) || ''}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AdminAnalyticsPage = () => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState('daily');

  const fetchAnalytics = async (p) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/analytics?period=${p}`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(period); }, [period]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totals = data?.totals || {};
  const chartData = chartView === 'monthly' ? (data?.monthly || []) : (data?.daily || []);

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Link to="/admin" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-6 transition-colors" data-testid="back-to-admin">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sales Analytics
            </h1>
            <p className="text-[#A1A7B5] text-sm mt-1">Track revenue, orders, and performance</p>
          </div>

          {/* Period Selector */}
          <div className="flex bg-[#0C0E14] rounded-lg p-1 border border-[#1E2532]">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-4 py-2 text-xs font-medium rounded-md transition-colors ${period === p.value ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="glass-card p-6" data-testid="total-revenue-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-[#A1A7B5] text-sm">Total Revenue</p>
            </div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ${totals.total_revenue?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="glass-card p-6" data-testid="total-orders-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-[#A1A7B5] text-sm">Total Orders</p>
            </div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {totals.total_orders || 0}
            </p>
          </div>
          <div className="glass-card p-6" data-testid="avg-order-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-[#A1A7B5] text-sm">Avg Order Value</p>
            </div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ${totals.avg_order_value?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-5 h-5 text-[#00F0FF]" />
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue</h2>
            </div>
            <div className="flex bg-[#111621] rounded-lg p-0.5">
              <button
                onClick={() => setChartView('daily')}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${chartView === 'daily' ? 'bg-[#1E2532] text-white' : 'text-[#A1A7B5]'}`}
                data-testid="chart-daily"
              >
                Daily
              </button>
              <button
                onClick={() => setChartView('monthly')}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${chartView === 'monthly' ? 'bg-[#1E2532] text-white' : 'text-[#A1A7B5]'}`}
                data-testid="chart-monthly"
              >
                Monthly
              </button>
            </div>
          </div>
          <BarChart data={chartData} dataKey="revenue" color="#00F0FF" height={240} />
        </div>

        {/* Orders Chart */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Calendar className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Orders</h2>
          </div>
          <BarChart data={chartData} dataKey="orders" color="#3B82F6" height={180} />
        </div>

        {/* Top Products */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Top Products
          </h2>
          {data?.top_products?.length > 0 ? (
            <div className="space-y-3">
              {data.top_products.map((p, i) => {
                const maxRev = data.top_products[0]?.revenue || 1;
                const pct = (p.revenue / maxRev) * 100;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`top-product-${i}`}>
                    <span className="text-[#A1A7B5] text-sm w-5 text-right">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white text-sm">{p._id}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-[#A1A7B5]">{p.quantity} sold</span>
                          <span className="text-[#00F0FF] font-semibold">${p.revenue?.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-[#111621] rounded-full h-2">
                        <div className="bg-[#00F0FF] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#A1A7B5] text-sm text-center py-8">No product data for this period</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
